import { useContext, useRef, useState } from 'react';
import { AuthContext } from '../context/authContext';
import { optionsPartido } from '../datos/options';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import encabezadoImg from '../components/informes/encabezado_A4.png';
import pieImg from '../components/informes/pie_hoja_A4.png';

// Convierte una imagen local a dataURL para poder incrustarla en el PDF
const cargarImagenComoDataUrl = (src) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement('canvas');
                c.width = img.naturalWidth;
                c.height = img.naturalHeight;
                c.getContext('2d').drawImage(img, 0, 0);
                resolve({ dataUrl: c.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
            } catch (e) {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });

// Formatea un número como moneda en pesos ($ 1.234.567,89)
const formatearMonto = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '$ 0,00';
    return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatearEntero = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR');
};

// Expediente del proyecto (objeto_o === "Proyecto"), igual que en los informes técnicos
const getExpedienteProyecto = (obra) => {
    const proyecto = (obra?.array_obras_expediente || []).find((item) => item && item.objeto_o === 'Proyecto');
    return proyecto?.expediente_o || 'Sin especificar';
};

const getAdendas = (obra) => {
    return (obra?.array_obras_expediente || [])
        .filter((item) => item && Number(item.monto_adenda) > 0)
        .reduce((acc, item) => acc + Number(item.monto_adenda), 0);
};

const getMontoTotalPagado = (obra) => {
    const pagos = [
        ...(obra?.pago_proyectos_jsonb || []),
        ...(obra?.pago_mejoras_jsonb || []),
        ...(obra?.pago_microcreditos_jsonb || []),
    ];
    return pagos.reduce((acc, p) => acc + Number(p.monto || 0), 0);
};

// "Adenda pagada": hay adenda en el expediente (monto_adenda) y además un
// pago de tipo "adenda" con fecha de pago efectiva (mismo criterio que el docx).
const tieneAdendaPagada = (obra) => {
    if (getAdendas(obra) <= 0) return false;
    const pagos = [
        ...(obra?.pago_proyectos_jsonb || []),
        ...(obra?.pago_mejoras_jsonb || []),
        ...(obra?.pago_microcreditos_jsonb || []),
    ];
    return pagos.some((p) => String(p?.tipo_pago || '').toLowerCase() === 'adenda' && Boolean(p?.fecha_pago));
};

// ¿Es un mejoramiento habitacional? (para el alcance: 1 vivienda ≈ 4 vecinas/os)
// Se detecta por el nombre de la obra o por el tipo/subtipo cargado en la obra.
const esMejoramientoHabitacional = (obra) => {
    const nombre = String(obra?.nombre_obra || '');
    if (/MEJORAMIENTOS?\s+HABITACIONAL/i.test(nombre)) return true;
    return (obra?.array_tipo_subtipo_proyecto || []).some((ts) =>
        /MEJORAMIENTOS?\s+HABITACIONAL/i.test(String(ts?.tipo || '')) ||
        /MEJORAMIENTOS?\s+HABITACIONAL/i.test(String(ts?.subtipo || ''))
    );
};

// Columna "Avance de obra y financiero" (mismo criterio que el docx original):
// si la obra tiene pagos o medición de avance → "Avance financiero: X% Avance de obra: Y%";
// si no tiene ninguno → el estado.
const getAvanceTexto = (obra) => {
    const monto = Number(obra?.monto_obra || 0);
    const pagado = getMontoTotalPagado(obra);
    const hayPagos = pagado > 0;
    const hayMedicion = obra?.avance_fisico != null;

    if (hayPagos || hayMedicion) {
        const avanceFin = monto > 0 ? Math.min(100, Math.round((pagado / monto) * 100)) : 0;
        const lineas = [`Avance financiero: ${avanceFin}%`];
        if (hayMedicion) {
            lineas.push(`Avance de obra: ${Math.round(Number(obra.avance_fisico))}%`);
        } else {
            lineas.push('Avance de obra: s/d');
        }
        return lineas;
    }

    const estado = obra?.estado && String(obra.estado).trim().length > 0 ? String(obra.estado).trim() : '—';
    return [estado];
};

const esMunicipio = (obra) => String(obra?.tipo_subsidio || '').toLowerCase().includes('municipio');

// Desglose "N proyectos: 1150 Mejoras habitacionales, 1 Proyectos de equipamiento, ..."
const getDesgloseProyectos = (obras) => {
    const porSubtipo = {};
    obras.forEach((obra) => {
        (obra?.array_tipo_subtipo_proyecto || []).forEach((ts) => {
            const label = ts?.subtipo?.trim() || ts?.tipo?.trim() || 'Proyectos';
            porSubtipo[label] = (porSubtipo[label] || 0) + Number(ts?.cantidad || 0);
        });
    });
    return Object.entries(porSubtipo).map(([label, cantidad]) => `${formatearEntero(cantidad)} ${label}`);
};

const getTotales = (obras) => {
    const inversion = obras.reduce((acc, o) => acc + Number(o.monto_obra || 0) + getAdendas(o), 0);
    const beneficiarios = obras.reduce((acc, o) => {
        const n = Number(String(o?.beneficiarios || '').replace(/[^\d]/g, ''));
        const base = Number.isFinite(n) ? n : 0;
        return acc + (esMejoramientoHabitacional(o) ? base * 4 : base);
    }, 0);
    return {
        inversion,
        proyectos: obras.length,
        beneficiarios,
    };
};

const InformeGestionPage = () => {
    const { token, role } = useContext(AuthContext);

    const [partido, setPartido] = useState('');
    const [obras, setObras] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [generado, setGenerado] = useState(false);
    const [error, setError] = useState('');
    const [descargando, setDescargando] = useState(false);

    const pdfRef = useRef(null);

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const partidoSeleccionado = optionsPartido.find((p) => p.value === partido);

    const generarInforme = async () => {
        if (!partido) {
            setError('Seleccioná un partido.');
            return;
        }
        setCargando(true);
        setError('');
        setGenerado(false);
        try {
            const res = await fetch(`/api/informe_gestion?partido=${encodeURIComponent(partidoSeleccionado?.label || '')}`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // No mostrar obras en estado "En guarda"
            const visibles = (data || []).filter((o) => String(o?.estado || '').trim().toLowerCase() !== 'en guarda');
            setObras(visibles);
            setGenerado(true);
            if (visibles.length === 0) {
                setError('No se encontraron obras para este partido.');
            }
        } catch (e) {
            console.error('Error al generar el informe:', e);
            setError(e.message);
        } finally {
            setCargando(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            setDescargando(true);
            const element = pdfRef.current;
            if (!element) return;

            const canvas = await html2canvas(element, {
                scale: 2,
                backgroundColor: '#ffffff',
                onclone: (doc) => {
                    doc.querySelectorAll('[data-pdf-omit="true"]').forEach((el) => el.remove());
                },
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const [encabezado, pie] = await Promise.all([
                cargarImagenComoDataUrl(encabezadoImg),
                cargarImagenComoDataUrl(pieImg),
            ]);
            const encabezadoHeight = encabezado ? pdfWidth * (encabezado.height / encabezado.width) : 0;
            const pieHeight = pie ? pdfWidth * (pie.height / pie.width) : 0;

            const margenH = 10;
            const margenTop = 10;
            const margenBottom = 10;
            const gapEncabezado = 4;
            const contentWidthMM = pdfWidth - 2 * margenH;
            const contentLeft = margenH;
            const yEncabezado = margenTop;
            const yContenido = yEncabezado + encabezadoHeight + gapEncabezado;
            const pieBottomY = pdfHeight - margenBottom - pieHeight;
            const contentHeightMM = pieBottomY - yContenido;

            const mmPerPx = contentWidthMM / canvas.width;
            const contentPagePx = contentHeightMM / mmPerPx;
            const totalPages = Math.max(1, Math.ceil(canvas.height / contentPagePx));

            for (let i = 0; i < totalPages; i++) {
                if (i > 0) pdf.addPage();

                const remaining = canvas.height - i * contentPagePx;
                const sliceH = Math.min(contentPagePx, remaining);
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = Math.ceil(sliceH);
                const ctx = pageCanvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                ctx.drawImage(canvas, 0, i * contentPagePx, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

                if (encabezado) {
                    pdf.addImage(encabezado.dataUrl, 'PNG', contentLeft, yEncabezado, contentWidthMM, encabezadoHeight);
                }

                pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', contentLeft, yContenido, contentWidthMM, sliceH * mmPerPx);

                if (pie) {
                    pdf.addImage(pie.dataUrl, 'PNG', contentLeft, pieBottomY, contentWidthMM, pieHeight);
                }
            }

            const nombrePartido = (partidoSeleccionado?.label || 'partido').replace(/\s+/g, '_');
            pdf.save(`informe_gestion_${nombrePartido}.pdf`);
        } catch (error) {
            console.error('Error al generar el PDF:', error);
        } finally {
            setDescargando(false);
        }
    };

    if (role !== 'admin') {
        return (
            <div className="p-8 text-center text-gray-500">
                Acceso denegado. Solo administradores.
            </div>
        );
    }

    const obrasMunicipio = obras.filter(esMunicipio);
    const obrasOrganizaciones = obras.filter((o) => !esMunicipio(o));
    const totales = getTotales(obras);

    return (
        <div className="p-6 w-full max-w-full">
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Informe de Gestión por Partido
            </h1>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                    <label htmlFor="partido" className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Partido
                    </label>
                    <select
                        id="partido"
                        value={partido}
                        onChange={(e) => setPartido(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 min-w-[260px]"
                    >
                        <option value="">Seleccionar partido...</option>
                        {optionsPartido.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={generarInforme}
                    disabled={cargando}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                    {cargando ? 'Generando...' : 'Generar informe'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">{error}</div>
            )}

            {generado && obras.length > 0 && (
                <div className="mb-4 flex justify-end">
                    <button
                        onClick={handleDownloadPDF}
                        disabled={descargando}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                    >
                        {descargando ? 'Generando PDF...' : 'Descargar PDF'}
                    </button>
                </div>
            )}

            {generado && obras.length > 0 && (
                <div className="bg-white p-8 px-10 sm:p-12 shadow-xl rounded-lg border border-gray-200">
                    <div ref={pdfRef} className="font-sans">
                        <div className="mb-6" data-pdf-omit="true">
                            <img src={encabezadoImg} alt="Encabezado del informe" className="w-full h-auto" />
                        </div>

                        <div className="text-center mb-8">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
                                Gestión Axel Kicillof.
                            </h1>
                            <h2 className="text-xl font-semibold text-gray-700">
                                {partidoSeleccionado?.label || ''}
                            </h2>
                            <h3 className="text-base font-medium text-gray-600 mt-1">
                                Informe de Gestión - Intervenciones Complementarias del Hábitat
                            </h3>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Inversión total
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearMonto(totales.inversion)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">
                                    Cantidad de proyectos
                                </p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(totales.proyectos)}</p>
                            </div>
                            <div className="border-2 border-teal-700 rounded-lg p-4 text-center">
                                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1">Alcance</p>
                                <p className="text-xl font-bold text-gray-900">{formatearEntero(totales.beneficiarios)}</p>
                            </div>
                        </div>

                        {totales.beneficiarios > 0 && (
                            <p className="text-sm text-gray-700 mb-8 text-center">
                                La TOTALIDAD DE LAS OBRAS beneficiará a{' '}
                                <strong className="font-semibold">{formatearEntero(totales.beneficiarios)}</strong>{' '}
                                vecinas y vecinos
                            </p>
                        )}

                        {obrasMunicipio.length > 0 && (
                            <SeccionInforme
                                titulo="Financiamiento para intervenciones complementarias del hábitat al municipio:"
                                obras={obrasMunicipio}
                                totales={{ inversion: getTotales(obrasMunicipio).inversion, proyectos: obrasMunicipio.length }}
                                conOrganismo={false}
                            />
                        )}

                        {obrasOrganizaciones.length > 0 && (
                            <SeccionInforme
                                titulo="Financiamiento para intervenciones complementarias del hábitat a organizaciones de la comunidad:"
                                obras={obrasOrganizaciones}
                                totales={{ inversion: getTotales(obrasOrganizaciones).inversion, proyectos: obrasOrganizaciones.length }}
                                conOrganismo
                            />
                        )}

                        <div className="mt-8 mb-6 flex justify-between items-center text-sm text-gray-500">
                            <span>Dirección Provincial de Intervenciones Complementarias</span>
                            <span>Fecha de salida: {new Date().toLocaleDateString('es-AR')}</span>
                        </div>

                        <div className="mb-6" data-pdf-omit="true">
                            <img src={pieImg} alt="Pie de página del informe" className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SeccionInforme = ({ titulo, obras, totales, conOrganismo = false }) => {
    const desglose = getDesgloseProyectos(obras);
    const cantOrgs = new Set(
        obras.map((o) =>
            String(o?.organismo_solicitante || o?.organismo_otro || '').trim().toLowerCase()
        ).filter(Boolean)
    ).size;

    return (
        <div className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">{titulo}</h3>
            <p className="text-sm font-medium text-gray-700 mb-1">
                {formatearMonto(totales.inversion)} total
            </p>
            <p className="text-sm text-gray-700 mb-4">
                {formatearEntero(totales.proyectos)} {totales.proyectos === 1 ? 'proyecto' : 'proyectos'}
                {conOrganismo && cantOrgs > 0 && (
                    <>: {formatearEntero(cantOrgs)} {cantOrgs === 1 ? 'organización' : 'organizaciones'}</>
                )}
                {desglose.length > 0 ? ` (${desglose.join(', ')})` : ''}
            </p>

            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-teal-700 text-white">
                        <th className="border border-teal-800 px-3 py-2 text-left font-medium">N° de Expediente</th>
                        {conOrganismo && (
                            <th className="border border-teal-800 px-3 py-2 text-left font-medium">
                                Organismo solicitante
                            </th>
                        )}
                        <th className="border border-teal-800 px-3 py-2 text-left font-medium">Nombre de la Obra</th>
                        <th className="border border-teal-800 px-3 py-2 text-left font-medium">Monto</th>
                        <th className="border border-teal-800 px-3 py-2 text-left font-medium">
                            Avance de obra y financiero
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {obras.map((obra, index) => {
                        const adendas = getAdendas(obra);
                        const monto = Number(obra?.monto_obra || 0);
                        return (
                            <tr key={obra?.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                    {getExpedienteProyecto(obra)}
                                </td>
                                {conOrganismo && (
                                    <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                        {obra?.organismo_solicitante || obra?.organismo_otro || '—'}
                                    </td>
                                )}
                                <td className="border border-gray-300 px-3 py-2 align-top text-gray-800">
                                    {obra?.nombre_obra || '—'}
                                </td>
                                <td className="border border-gray-300 px-3 py-2 align-top text-right text-gray-800 whitespace-nowrap">
                                    {adendas > 0 ? (
                                        <>
                                            <span className="block">{formatearMonto(monto)}</span>
                                            <span className="block text-xs text-gray-600">
                                                + adenda por {formatearMonto(adendas)}
                                            </span>
                                            <span className="block text-xs font-medium text-gray-800">
                                                Total: {formatearMonto(monto + adendas)}
                                            </span>
                                        </>
                                    ) : (
                                        formatearMonto(monto)
                                    )}
                                </td>
                                <td className="border border-gray-300 px-3 py-2 align-top text-gray-800 whitespace-nowrap">
                                    {getAvanceTexto(obra).map((linea) => (
                                        <div key={linea}>{linea}</div>
                                    ))}
                                    {tieneAdendaPagada(obra) && (
                                        <div className="text-xs font-medium text-teal-700 mt-1">
                                            Adenda pagada
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default InformeGestionPage;